import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Tooltip,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
  Menu,
  Divider,
  InputAdornment,
} from '@mui/material';
import {
  PersonAdd as PersonAddIcon,
  ContentCopy as ContentCopyIcon,
  MoreVert as MoreVertIcon,
  Block as BlockIcon,
  RestartAlt as RestoreIcon,
  Close as CloseIcon,
  Check as CheckIcon,
  Person as PersonIcon,
  MailOutline as MailIcon,
  PersonOutline as PersonOutlineIcon,
  SwapHoriz as SwapHorizIcon,
  Payments as PaymentsIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';
import {
  listStaffUsers,
  listInvitations,
  createInvitation,
  revokeInvitation,
  deleteInvitation,
  updateStaffRole,
  setStaffDisabled,
  removeStaffUser,
  addStaffPayment,
  removeStaffPayment,
  buildInviteLink,
  countUnownedDocs,
  backfillOwnership,
} from '../../services/staff';
import { getAllClients } from '../../services/firebase';
import {
  StaffUser,
  Invitation,
  Role,
  ROLES,
  ROLE_LABEL,
  ROLE_DESCRIPTION,
} from '../../types/staff';
import { formatCurrency, formatDate } from '../../utils/formatters';
import { computeMetrics, OwnerMetrics } from '../../utils/staffMetrics';

const initialsFromEmail = (name: string | undefined, email: string): string => {
  const src = name?.trim() || email;
  const parts = src.split(/[\s@]+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const a = parts[0]?.[0] ?? '';
  const b = parts.length > 1 ? parts[parts.length - 1]?.[0] ?? '' : '';
  return (a + b).toUpperCase() || a.toUpperCase();
};

const avatarColorFor = (key: string): string => {
  const palette = ['#1E3A5F', '#3B82F6', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899', '#0EA5E9', '#14B8A6'];
  let hash = 0;
  for (let i = 0; i < key.length; i++) hash = (hash * 31 + key.charCodeAt(i)) | 0;
  return palette[Math.abs(hash) % palette.length];
};

const ROLE_CHIP_COLOR: Record<Role, { bg: string; fg: string }> = {
  admin: { bg: '#FEF3C7', fg: '#92400E' },
  manager: { bg: '#EFF6FF', fg: '#1D4ED8' },
  staff: { bg: '#F3F4F6', fg: '#374151' },
};

const Staff: React.FC = () => {
  const { staffUser } = useAuth();
  const [users, setUsers] = useState<StaffUser[]>([]);
  const [invites, setInvites] = useState<Invitation[]>([]);
  const [loading, setLoading] = useState(true);

  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [linkDialog, setLinkDialog] = useState<{ invite: Invitation } | null>(null);
  const [userMenu, setUserMenu] = useState<{
    anchor: HTMLElement;
    user: StaffUser;
  } | null>(null);
  const [roleDialog, setRoleDialog] = useState<{ user: StaffUser; newRole: Role } | null>(
    null,
  );
  const [removeDialog, setRemoveDialog] = useState<{ user: StaffUser } | null>(null);

  // Staff-pay ledger (manual running total). The dialog both records new
  // payments and lists/removes prior ones for the selected staff member.
  const [payDialog, setPayDialog] = useState<{ user: StaffUser } | null>(null);
  const [payAmount, setPayAmount] = useState('');
  const [payDate, setPayDate] = useState('');
  const [payNote, setPayNote] = useState('');
  const [savingPay, setSavingPay] = useState(false);
  const [removingPayId, setRemovingPayId] = useState<string | null>(null);

  // Per-owner financial roll-up (collected / balance / paid tasks), keyed by uid.
  const [metricsByOwner, setMetricsByOwner] = useState<Record<string, OwnerMetrics>>({});
  const [metricsLoading, setMetricsLoading] = useState(true);

  // Backfill state
  const [unowned, setUnowned] = useState<{ clients: number; cases: number } | null>(null);
  const [unownedLoading, setUnownedLoading] = useState(false);
  const [backfilling, setBackfilling] = useState(false);
  const [backfillDialogOpen, setBackfillDialogOpen] = useState(false);
  const [backfillOwnerId, setBackfillOwnerId] = useState<string>('');

  const reload = async () => {
    setLoading(true);
    try {
      const [u, i] = await Promise.all([listStaffUsers(), listInvitations()]);
      setUsers(u);
      setInvites(i);
    } catch (err) {
      console.error('Failed to load staff/invitations:', err);
      toast.error('Failed to load staff data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
  }, []);

  // Load every client (admin can read all) and aggregate collected / balance
  // by owner uid. Runs once; these aggregates are derived purely from client
  // cases and are independent of the manual staff-pay ledger, so recording a
  // payment never requires re-running this.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setMetricsLoading(true);
      try {
        const clients = await getAllClients(false);
        const byOwner: Record<string, typeof clients> = {};
        for (const c of clients) {
          const owner = c.createdBy || 'unassigned';
          (byOwner[owner] = byOwner[owner] || []).push(c);
        }
        const agg: Record<string, OwnerMetrics> = {};
        for (const owner of Object.keys(byOwner)) {
          agg[owner] = computeMetrics(byOwner[owner]);
        }
        if (!cancelled) setMetricsByOwner(agg);
      } catch (err) {
        console.error('Failed to load staff metrics:', err);
      } finally {
        if (!cancelled) setMetricsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Lazily count unowned docs once on mount (admin-only path).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setUnownedLoading(true);
      try {
        const counts = await countUnownedDocs();
        if (!cancelled) setUnowned(counts);
      } catch (err) {
        console.error('Failed to count unowned docs:', err);
        if (!cancelled) setUnowned(null);
      } finally {
        if (!cancelled) setUnownedLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleBackfill = async () => {
    const targetUid = backfillOwnerId || staffUser?.id;
    if (!targetUid) return;
    setBackfilling(true);
    try {
      const updated = await backfillOwnership(targetUid);
      const targetUser = users.find((u) => u.id === targetUid);
      const label = targetUser?.email ?? 'selected user';
      toast.success(
        `Migration complete: ${updated.clients} clients, ${updated.cases} cases assigned to ${label}.`,
      );
      setUnowned({ clients: 0, cases: 0 });
      setBackfillDialogOpen(false);
    } catch (err) {
      console.error('Backfill failed:', err);
      toast.error('Ownership migration failed');
    } finally {
      setBackfilling(false);
    }
  };

  const pendingInvites = useMemo(
    () => invites.filter((inv) => inv.status === 'pending'),
    [invites],
  );

  /* ------- handlers ------- */

  const handleChangeRole = async () => {
    if (!roleDialog) return;
    try {
      await updateStaffRole(roleDialog.user.id, roleDialog.newRole);
      toast.success(`${roleDialog.user.email} is now ${ROLE_LABEL[roleDialog.newRole]}`);
      setRoleDialog(null);
      reload();
    } catch (err) {
      console.error(err);
      toast.error('Failed to change role');
    }
  };

  const openPayDialog = (user: StaffUser) => {
    const latest = users.find((u) => u.id === user.id) ?? user;
    setPayDialog({ user: latest });
    setPayAmount('');
    setPayNote('');
    setPayDate(new Date().toISOString().split('T')[0]);
  };

  // Refresh the users list (picks up new staffPayTotal/staffPayments) without
  // the full-page loading skeleton, and keep the open dialog's user in sync.
  // Uses a functional update so a dialog the admin already closed mid-refresh
  // is never resurrected.
  const refreshUsersInto = async (uid: string) => {
    const refreshed = await listStaffUsers();
    setUsers(refreshed);
    const updated = refreshed.find((u) => u.id === uid);
    setPayDialog((prev) => (prev && updated ? { user: updated } : prev));
  };

  const handleAddPayment = async () => {
    if (!payDialog) return;
    const amount = parseFloat(payAmount.trim());
    if (isNaN(amount) || amount <= 0) {
      toast.error('Enter a payment amount greater than 0');
      return;
    }
    if (!payDate) {
      toast.error('Pick a payment date');
      return;
    }
    setSavingPay(true);
    try {
      await addStaffPayment(
        payDialog.user.id,
        amount,
        payDate,
        payNote,
        staffUser?.id,
      );
      toast.success(
        `Recorded ${formatCurrency(amount)} for ${payDialog.user.email}`,
      );
      await refreshUsersInto(payDialog.user.id);
      setPayAmount('');
      setPayNote('');
    } catch (err) {
      console.error(err);
      toast.error('Failed to record payment');
    } finally {
      setSavingPay(false);
    }
  };

  const handleRemovePayment = async (paymentId: string) => {
    if (!payDialog) return;
    setRemovingPayId(paymentId);
    try {
      await removeStaffPayment(payDialog.user.id, paymentId);
      await refreshUsersInto(payDialog.user.id);
      toast.success('Payment removed');
    } catch (err) {
      console.error(err);
      toast.error('Failed to remove payment');
    } finally {
      setRemovingPayId(null);
    }
  };

  const handleToggleDisabled = async (user: StaffUser) => {
    setUserMenu(null);
    try {
      await setStaffDisabled(user.id, !user.disabled);
      toast.success(
        user.disabled ? `${user.email} re-enabled` : `${user.email} disabled`,
      );
      reload();
    } catch (err) {
      console.error(err);
      toast.error('Failed to update user');
    }
  };

  const handleRemoveUser = async () => {
    if (!removeDialog) return;
    try {
      await removeStaffUser(removeDialog.user.id);
      toast.success(`${removeDialog.user.email} removed`);
      setRemoveDialog(null);
      reload();
    } catch (err) {
      console.error(err);
      toast.error('Failed to remove user');
    }
  };

  const handleRevokeInvite = async (invite: Invitation) => {
    try {
      await revokeInvitation(invite.id);
      toast.success('Invitation revoked');
      reload();
    } catch (err) {
      console.error(err);
      toast.error('Failed to revoke invitation');
    }
  };

  const handleDeleteInvite = async (invite: Invitation) => {
    try {
      await deleteInvitation(invite.id);
      toast.success('Invitation deleted');
      reload();
    } catch (err) {
      console.error(err);
      toast.error('Failed to delete invitation');
    }
  };

  const copyLink = (token: string) => {
    const link = buildInviteLink(token);
    if (navigator.clipboard) {
      navigator.clipboard.writeText(link).then(
        () => toast.success('Invite link copied'),
        () => toast.error('Could not copy link'),
      );
    } else {
      toast.info(link);
    }
  };

  // Keep the member's identity visible while horizontally scrolling the wide
  // (6-column) Active Staff table on small screens — mirrors Dashboard/ClientList.
  const stickyFirstHead = {
    position: { xs: 'sticky' as const, md: 'static' as const },
    left: 0,
    zIndex: 3,
    backgroundColor: '#FFFFFF',
  };
  const stickyFirstBody = {
    position: { xs: 'sticky' as const, md: 'static' as const },
    left: 0,
    zIndex: 1,
    backgroundColor: '#FFFFFF',
  };

  /* ------- skeletons ------- */

  const UserRowSkeleton = () => (
    <>
      {Array.from({ length: 3 }).map((_, i) => (
        <TableRow key={i} sx={{ '& td': { height: 56 } }}>
          <TableCell>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Skeleton variant="circular" width={32} height={32} />
              <Skeleton variant="text" width={180} />
            </Box>
          </TableCell>
          <TableCell>
            <Skeleton variant="rounded" width={70} height={22} />
          </TableCell>
          <TableCell>
            <Skeleton variant="text" width={100} />
          </TableCell>
          <TableCell align="right">
            <Skeleton variant="rounded" width={28} height={28} sx={{ ml: 'auto' }} />
          </TableCell>
        </TableRow>
      ))}
    </>
  );

  const StaffRowSkeleton = () => (
    <>
      {Array.from({ length: 3 }).map((_, i) => (
        <TableRow key={i} sx={{ '& td': { height: 56 } }}>
          <TableCell>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Skeleton variant="circular" width={32} height={32} />
              <Skeleton variant="text" width={160} />
            </Box>
          </TableCell>
          <TableCell>
            <Skeleton variant="rounded" width={70} height={22} />
          </TableCell>
          {Array.from({ length: 3 }).map((__, j) => (
            <TableCell key={j} align="right">
              <Skeleton variant="text" width={60} sx={{ ml: 'auto' }} />
            </TableCell>
          ))}
          <TableCell align="right">
            <Skeleton variant="rounded" width={28} height={28} sx={{ ml: 'auto' }} />
          </TableCell>
        </TableRow>
      ))}
    </>
  );

  return (
    <Box
      sx={{
        maxWidth: 1280,
        mx: 'auto',
        px: { xs: 4, sm: 6, md: 8 },
        py: { xs: 5, md: 6 },
      }}
    >
      <Box
        sx={{
          mb: 6,
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: 3,
        }}
      >
        <Box>
          <Typography
            component="h1"
            sx={{ fontSize: '1.25rem', fontWeight: 600, color: 'text.primary', mb: 0.5 }}
          >
            Staff
          </Typography>
          <Typography sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
            Manage who can sign in and what they can do
          </Typography>
        </Box>
        <Button
          startIcon={<PersonAddIcon />}
          variant="contained"
          color="primary"
          onClick={() => setInviteDialogOpen(true)}
        >
          Invite Staff
        </Button>
      </Box>

      {/* Ownership migration banner — only renders when there are unowned docs */}
      {!unownedLoading && unowned && (unowned.clients > 0 || unowned.cases > 0) && (
        <Alert
          severity="warning"
          icon={<SwapHorizIcon sx={{ fontSize: 20 }} />}
          action={
            <Button
              size="small"
              variant="contained"
              color="primary"
              onClick={() => setBackfillDialogOpen(true)}
            >
              Migrate ownership
            </Button>
          }
          sx={{ mb: 5, alignItems: 'flex-start' }}
        >
          <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, mb: 0.5 }}>
            Legacy data needs an owner
          </Typography>
          <Typography sx={{ fontSize: '0.8125rem' }}>
            {unowned.clients} {unowned.clients === 1 ? 'client' : 'clients'} and{' '}
            {unowned.cases} {unowned.cases === 1 ? 'case' : 'cases'} were created
            before ownership tracking. Assign them all to you so staff visibility
            rules work correctly.
          </Typography>
        </Alert>
      )}

      {/* Active staff */}
      <Paper
        sx={{
          width: '100%',
          mb: 5,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            px: { xs: 4, sm: 5 },
            py: 3,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography
            component="h2"
            sx={{ fontSize: '0.9375rem', fontWeight: 600, color: 'text.primary' }}
          >
            Active Staff
          </Typography>
          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
            {loading ? '—' : `${users.length} ${users.length === 1 ? 'member' : 'members'}`}
          </Typography>
        </Box>
        <TableContainer>
          <Table aria-label="Active staff" sx={{ minWidth: 880 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={stickyFirstHead}>User</TableCell>
                <TableCell>Role</TableCell>
                <TableCell align="right">Collected</TableCell>
                <TableCell align="right">Balance</TableCell>
                <TableCell align="right">Staff Pay</TableCell>
                <TableCell align="right" sx={{ width: 80 }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <StaffRowSkeleton />
              ) : users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} sx={{ p: 0, borderBottom: 'none' }}>
                    <Box sx={{ textAlign: 'center', py: 10, px: 4 }}>
                      <Typography sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
                        No staff yet. Use Invite Staff to add the first member.
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                users.map((u) => {
                  const initials = initialsFromEmail(u.displayName, u.email);
                  const colors = ROLE_CHIP_COLOR[u.role];
                  const isSelf = staffUser?.id === u.id;
                  const isStaffRole = u.role === 'staff';
                  const m = metricsByOwner[u.id];
                  const staffPayTotal = u.staffPayTotal ?? 0;
                  const staffPayCount = u.staffPayments?.length ?? 0;
                  return (
                    <TableRow key={u.id} hover>
                      <TableCell sx={stickyFirstBody}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Avatar
                            sx={{
                              width: 32,
                              height: 32,
                              bgcolor: avatarColorFor(u.email),
                              fontSize: '0.75rem',
                              color: '#FFFFFF',
                              opacity: u.disabled ? 0.5 : 1,
                            }}
                          >
                            {initials}
                          </Avatar>
                          <Box sx={{ minWidth: 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography
                                sx={{
                                  fontSize: '0.875rem',
                                  fontWeight: 500,
                                  color: 'text.primary',
                                }}
                              >
                                {u.displayName || u.email.split('@')[0]}
                              </Typography>
                              {isSelf && (
                                <Chip
                                  label="You"
                                  size="small"
                                  sx={{
                                    height: 18,
                                    bgcolor: '#F3F4F6',
                                    color: '#6B7280',
                                    fontSize: '0.65rem',
                                  }}
                                />
                              )}
                              {u.disabled && (
                                <Chip
                                  label="Disabled"
                                  size="small"
                                  sx={{
                                    height: 18,
                                    bgcolor: '#FEE2E2',
                                    color: '#991B1B',
                                    fontSize: '0.65rem',
                                  }}
                                />
                              )}
                            </Box>
                            <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                              {u.email}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={ROLE_LABEL[u.role]}
                          size="small"
                          sx={{ bgcolor: colors.bg, color: colors.fg, fontWeight: 600 }}
                        />
                      </TableCell>
                      {/* Total collected on this member's clients */}
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {metricsLoading ? (
                          <Skeleton variant="text" width={56} sx={{ ml: 'auto' }} />
                        ) : (
                          <Typography sx={{ fontSize: '0.875rem', color: 'text.primary' }}>
                            {formatCurrency(m?.totalCollected ?? 0)}
                          </Typography>
                        )}
                      </TableCell>
                      {/* Outstanding balance */}
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {metricsLoading ? (
                          <Skeleton variant="text" width={56} sx={{ ml: 'auto' }} />
                        ) : (
                          <Typography
                            sx={{
                              fontSize: '0.875rem',
                              color:
                                (m?.totalBalance ?? 0) > 0 ? 'text.primary' : 'text.secondary',
                            }}
                          >
                            {formatCurrency(m?.totalBalance ?? 0)}
                          </Typography>
                        )}
                      </TableCell>
                      {/* Staff pay — manual running total recorded by admins */}
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {!isStaffRole ? (
                          <Typography sx={{ color: 'text.disabled' }}>—</Typography>
                        ) : (
                          <Box
                            component="button"
                            onClick={() => openPayDialog(u)}
                            sx={{
                              background: 'none',
                              border: 'none',
                              p: 0,
                              cursor: 'pointer',
                              fontFamily: 'inherit',
                              textAlign: 'right',
                              '&:hover .staff-pay-amount': { textDecoration: 'underline' },
                            }}
                            aria-label={`Manage pay for ${u.email}`}
                          >
                            <Typography
                              component="span"
                              className="staff-pay-amount"
                              sx={{
                                display: 'block',
                                fontSize: '0.875rem',
                                fontWeight: 600,
                                color: staffPayTotal > 0 ? 'text.primary' : 'primary.main',
                              }}
                            >
                              {staffPayTotal > 0 ? formatCurrency(staffPayTotal) : 'Add pay'}
                            </Typography>
                            {staffPayCount > 0 && (
                              <Typography
                                component="span"
                                sx={{ display: 'block', fontSize: '0.7rem', color: 'text.secondary' }}
                              >
                                {staffPayCount} {staffPayCount === 1 ? 'payment' : 'payments'}
                              </Typography>
                            )}
                          </Box>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={(e) => setUserMenu({ anchor: e.currentTarget, user: u })}
                          aria-label={`Actions for ${u.email}`}
                          disabled={isSelf}
                        >
                          <MoreVertIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Pending invitations */}
      <Paper
        sx={{
          width: '100%',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            px: { xs: 4, sm: 5 },
            py: 3,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography
            component="h2"
            sx={{ fontSize: '0.9375rem', fontWeight: 600, color: 'text.primary' }}
          >
            Pending Invitations
          </Typography>
          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
            {loading
              ? '—'
              : `${pendingInvites.length} ${pendingInvites.length === 1 ? 'invite' : 'invites'}`}
          </Typography>
        </Box>
        <TableContainer>
          <Table aria-label="Pending invitations">
            <TableHead>
              <TableRow>
                <TableCell>Email</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Expires</TableCell>
                <TableCell align="right" sx={{ width: 200 }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <UserRowSkeleton />
              ) : pendingInvites.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} sx={{ p: 0, borderBottom: 'none' }}>
                    <Box sx={{ textAlign: 'center', py: 10, px: 4 }}>
                      <Typography sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
                        No pending invitations.
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                pendingInvites.map((inv) => {
                  const colors = ROLE_CHIP_COLOR[inv.role];
                  const expired = inv.expiresAt.toMillis() < Date.now();
                  return (
                    <TableRow key={inv.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Box
                            sx={{
                              width: 32,
                              height: 32,
                              borderRadius: '50%',
                              bgcolor: '#EFF6FF',
                              color: '#1D4ED8',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <MailIcon sx={{ fontSize: 16 }} />
                          </Box>
                          <Box>
                            <Typography sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
                              {inv.email}
                            </Typography>
                            <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                              Invited {formatDate(inv.createdAt.toDate())}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={ROLE_LABEL[inv.role]}
                          size="small"
                          sx={{ bgcolor: colors.bg, color: colors.fg, fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell sx={{ color: expired ? 'error.main' : 'text.secondary' }}>
                        {expired ? 'Expired' : formatDate(inv.expiresAt.toDate())}
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'inline-flex', gap: 0.5 }}>
                          <Tooltip title="Copy invite link" arrow>
                            <IconButton size="small" onClick={() => copyLink(inv.id)}>
                              <ContentCopyIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Show link" arrow>
                            <IconButton
                              size="small"
                              onClick={() => setLinkDialog({ invite: inv })}
                            >
                              <PersonOutlineIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Revoke" arrow>
                            <IconButton
                              size="small"
                              onClick={() => handleRevokeInvite(inv)}
                              sx={{ color: 'text.secondary' }}
                            >
                              <BlockIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete" arrow>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteInvite(inv)}
                              sx={{ color: '#DC2626' }}
                            >
                              <CloseIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Invite dialog */}
      <InviteDialog
        open={inviteDialogOpen}
        onClose={() => setInviteDialogOpen(false)}
        onCreated={(inv) => {
          setInviteDialogOpen(false);
          reload();
          setLinkDialog({ invite: inv });
        }}
        createdBy={staffUser?.id ?? ''}
        createdByEmail={staffUser?.email}
      />

      {/* Invite-link dialog */}
      <Dialog
        open={!!linkDialog}
        onClose={() => setLinkDialog(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Invitation link
          <IconButton
            onClick={() => setLinkDialog(null)}
            aria-label="Close"
            size="small"
            sx={{ position: 'absolute', right: 12, top: 12, color: 'text.secondary' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {linkDialog && (
            <>
              <Alert severity="info" sx={{ mb: 3, alignItems: 'flex-start' }}>
                <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, mb: 0.5 }}>
                  Share this link with {linkDialog.invite.email}
                </Typography>
                <Typography sx={{ fontSize: '0.8125rem' }}>
                  They'll set up their password and join as{' '}
                  <strong>{ROLE_LABEL[linkDialog.invite.role]}</strong>. The link expires{' '}
                  {formatDate(linkDialog.invite.expiresAt.toDate())}.
                </Typography>
              </Alert>
              <TextField
                fullWidth
                value={buildInviteLink(linkDialog.invite.id)}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <Button
                        size="small"
                        startIcon={<ContentCopyIcon sx={{ fontSize: 16 }} />}
                        onClick={() => copyLink(linkDialog.invite.id)}
                      >
                        Copy
                      </Button>
                    </InputAdornment>
                  ),
                }}
                onFocus={(e) => e.currentTarget.select()}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="contained" color="primary" onClick={() => setLinkDialog(null)}>
            Done
          </Button>
        </DialogActions>
      </Dialog>

      {/* Per-user actions menu */}
      <Menu
        anchorEl={userMenu?.anchor ?? null}
        open={Boolean(userMenu)}
        onClose={() => setUserMenu(null)}
        PaperProps={{
          elevation: 0,
          sx: {
            mt: 0.5,
            minWidth: 200,
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: '0 10px 25px rgba(0,0,0,0.08)',
            borderRadius: 2,
          },
        }}
      >
        {ROLES.map((r) => {
          const isCurrent = userMenu?.user.role === r;
          return (
            <MenuItem
              key={r}
              onClick={() => {
                if (!userMenu) return;
                if (isCurrent) {
                  setUserMenu(null);
                  return;
                }
                setRoleDialog({ user: userMenu.user, newRole: r });
                setUserMenu(null);
              }}
              sx={{ fontSize: '0.875rem', gap: 1.5 }}
            >
              <Box
                component="span"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 18,
                  height: 18,
                }}
              >
                {isCurrent && <CheckIcon sx={{ fontSize: 16 }} />}
              </Box>
              {ROLE_LABEL[r]}
            </MenuItem>
          );
        })}
        <Divider />
        {userMenu?.user.role === 'staff' && (
          <MenuItem
            onClick={() => {
              if (userMenu) openPayDialog(userMenu.user);
              setUserMenu(null);
            }}
            sx={{ fontSize: '0.875rem', gap: 1.5 }}
          >
            <PaymentsIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
            Record / manage pay
          </MenuItem>
        )}
        {userMenu?.user.disabled ? (
          <MenuItem
            onClick={() => userMenu && handleToggleDisabled(userMenu.user)}
            sx={{ fontSize: '0.875rem', gap: 1.5 }}
          >
            <RestoreIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
            Re-enable
          </MenuItem>
        ) : (
          <MenuItem
            onClick={() => userMenu && handleToggleDisabled(userMenu.user)}
            sx={{ fontSize: '0.875rem', gap: 1.5 }}
          >
            <BlockIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
            Disable
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            if (userMenu) setRemoveDialog({ user: userMenu.user });
            setUserMenu(null);
          }}
          sx={{ fontSize: '0.875rem', gap: 1.5, color: '#DC2626' }}
        >
          <PersonIcon sx={{ fontSize: 18 }} />
          Remove from staff
        </MenuItem>
      </Menu>

      {/* Confirm role change */}
      <Dialog
        open={!!roleDialog}
        onClose={() => setRoleDialog(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          Change role?
          <IconButton
            onClick={() => setRoleDialog(null)}
            aria-label="Close"
            size="small"
            sx={{ position: 'absolute', right: 12, top: 12, color: 'text.secondary' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {roleDialog && (
            <>
              <Typography sx={{ fontSize: '0.875rem', color: 'text.secondary', mb: 2 }}>
                <strong>{roleDialog.user.email}</strong> will be set to{' '}
                <strong>{ROLE_LABEL[roleDialog.newRole]}</strong>.
              </Typography>
              <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>
                {ROLE_DESCRIPTION[roleDialog.newRole]}
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => setRoleDialog(null)}>
            Cancel
          </Button>
          <Button variant="contained" color="primary" onClick={handleChangeRole}>
            Change role
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm remove */}
      <Dialog
        open={!!removeDialog}
        onClose={() => setRemoveDialog(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          Remove {removeDialog?.user.email}?
          <IconButton
            onClick={() => setRemoveDialog(null)}
            aria-label="Close"
            size="small"
            sx={{ position: 'absolute', right: 12, top: 12, color: 'text.secondary' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '0.875rem', color: 'text.secondary', mb: 2 }}>
            They will be signed out and unable to log back in. Their Firebase Auth
            account remains, but they have no access to the app until a new invitation
            is accepted.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => setRemoveDialog(null)}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={handleRemoveUser}>
            Remove
          </Button>
        </DialogActions>
      </Dialog>

      {/* Staff pay — manual running total (record / manage payments) */}
      <Dialog
        open={!!payDialog}
        onClose={() => !savingPay && !removingPayId && setPayDialog(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Staff pay
          {!savingPay && !removingPayId && (
            <IconButton
              onClick={() => setPayDialog(null)}
              aria-label="Close"
              size="small"
              sx={{ position: 'absolute', right: 12, top: 12, color: 'text.secondary' }}
            >
              <CloseIcon />
            </IconButton>
          )}
        </DialogTitle>
        <DialogContent>
          {payDialog &&
            (() => {
              const payments = [...(payDialog.user.staffPayments ?? [])].sort((a, b) =>
                a.date === b.date
                  ? b.id.localeCompare(a.id)
                  : a.date < b.date
                  ? 1
                  : -1,
              );
              const total = payDialog.user.staffPayTotal ?? 0;
              return (
                <>
                  {/* Running total */}
                  <Box
                    sx={{
                      px: 2.5,
                      py: 2,
                      mb: 3,
                      borderRadius: 2,
                      bgcolor: '#EFF6FF',
                      border: '1px solid #BFDBFE',
                    }}
                  >
                    <Typography sx={{ fontSize: '0.75rem', color: '#1D4ED8', fontWeight: 600 }}>
                      Total paid to {payDialog.user.displayName || payDialog.user.email}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '1.5rem',
                        fontWeight: 700,
                        color: '#1E3A5F',
                        fontVariantNumeric: 'tabular-nums',
                        lineHeight: 1.3,
                      }}
                    >
                      {formatCurrency(total)}
                    </Typography>
                    <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                      {payments.length} {payments.length === 1 ? 'payment' : 'payments'} recorded
                    </Typography>
                  </Box>

                  {/* Record a payment */}
                  <Typography
                    sx={{ fontSize: '0.8125rem', fontWeight: 600, color: 'text.primary', mb: 1.5 }}
                  >
                    Record a payment
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      gap: 2,
                      mb: 2,
                    }}
                  >
                    <TextField
                      label="Amount"
                      type="number"
                      size="small"
                      autoFocus
                      value={payAmount}
                      onChange={(e) => setPayAmount(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleAddPayment();
                      }}
                      disabled={savingPay}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                      }}
                      inputProps={{ min: 0, step: '0.01' }}
                      sx={{ flex: 1 }}
                    />
                    <TextField
                      label="Date"
                      type="date"
                      size="small"
                      value={payDate}
                      onChange={(e) => setPayDate(e.target.value)}
                      disabled={savingPay}
                      InputLabelProps={{ shrink: true }}
                      sx={{ flex: 1 }}
                    />
                  </Box>
                  <TextField
                    label="Note (optional)"
                    size="small"
                    fullWidth
                    value={payNote}
                    onChange={(e) => setPayNote(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleAddPayment();
                    }}
                    disabled={savingPay}
                    placeholder="e.g. June commission"
                    sx={{ mb: 2 }}
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleAddPayment}
                    disabled={savingPay}
                    startIcon={
                      savingPay ? (
                        <CircularProgress size={16} sx={{ color: '#FFFFFF' }} />
                      ) : (
                        <PaymentsIcon sx={{ fontSize: 18 }} />
                      )
                    }
                  >
                    {savingPay ? 'Adding…' : 'Add payment'}
                  </Button>

                  {/* History */}
                  <Divider sx={{ my: 3 }} />
                  <Typography
                    sx={{ fontSize: '0.8125rem', fontWeight: 600, color: 'text.primary', mb: 1.5 }}
                  >
                    Payment history
                  </Typography>
                  {payments.length === 0 ? (
                    <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary', py: 2 }}>
                      No payments recorded yet.
                    </Typography>
                  ) : (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1,
                        maxHeight: 220,
                        overflowY: 'auto',
                      }}
                    >
                      {payments.map((p) => (
                        <Box
                          key={p.id}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 1.5,
                            px: 2,
                            py: 1.25,
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: 'divider',
                          }}
                        >
                          <Box sx={{ minWidth: 0 }}>
                            <Typography
                              sx={{
                                fontSize: '0.875rem',
                                fontWeight: 600,
                                color: 'text.primary',
                                fontVariantNumeric: 'tabular-nums',
                              }}
                            >
                              {formatCurrency(Number(p.amount) || 0)}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: '0.7rem',
                                color: 'text.secondary',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {p.date ? formatDate(p.date) : '—'}
                              {p.note ? ` · ${p.note}` : ''}
                            </Typography>
                          </Box>
                          <Tooltip title="Remove payment" arrow>
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => handleRemovePayment(p.id)}
                                disabled={removingPayId === p.id || savingPay}
                                sx={{ color: '#DC2626' }}
                                aria-label="Remove payment"
                              >
                                {removingPayId === p.id ? (
                                  <CircularProgress size={16} />
                                ) : (
                                  <CloseIcon sx={{ fontSize: 16 }} />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      ))}
                    </Box>
                  )}
                </>
              );
            })()}
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setPayDialog(null)}
            disabled={savingPay || !!removingPayId}
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm ownership backfill */}
      <Dialog
        open={backfillDialogOpen}
        onClose={() => !backfilling && setBackfillDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        TransitionProps={{
          onEnter: () => {
            // Default the assignment to the admin running the migration.
            if (!backfillOwnerId && staffUser?.id) {
              setBackfillOwnerId(staffUser.id);
            }
          },
        }}
      >
        <DialogTitle>
          Migrate ownership?
          {!backfilling && (
            <IconButton
              onClick={() => setBackfillDialogOpen(false)}
              aria-label="Close"
              size="small"
              sx={{ position: 'absolute', right: 12, top: 12, color: 'text.secondary' }}
            >
              <CloseIcon />
            </IconButton>
          )}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '0.875rem', color: 'text.secondary', mb: 3 }}>
            Pick the staff member who should receive ownership of every client
            and case that is missing a <code>createdBy</code> field. Running this
            again later is safe — only docs that still don't have an owner are
            touched.
          </Typography>

          <Box sx={{ mb: 3 }}>
            <Typography
              component="label"
              htmlFor="backfill-owner"
              sx={{
                display: 'block',
                fontSize: '0.8125rem',
                fontWeight: 500,
                color: 'text.primary',
                mb: 1,
              }}
            >
              Assign to
            </Typography>
            <FormControl fullWidth size="small">
              <Select
                id="backfill-owner"
                value={backfillOwnerId}
                onChange={(e) => setBackfillOwnerId(e.target.value as string)}
                displayEmpty
                disabled={backfilling || users.length === 0}
              >
                {users
                  .filter((u) => !u.disabled)
                  .map((u) => (
                    <MenuItem key={u.id} value={u.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
                            {u.displayName || u.email.split('@')[0]}
                            {u.id === staffUser?.id && (
                              <Box component="span" sx={{ ml: 0.75, fontSize: '0.7rem', color: 'text.secondary' }}>
                                (you)
                              </Box>
                            )}
                          </Typography>
                          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                            {u.email}
                          </Typography>
                        </Box>
                        <Chip
                          label={ROLE_LABEL[u.role]}
                          size="small"
                          sx={{
                            bgcolor: ROLE_CHIP_COLOR[u.role].bg,
                            color: ROLE_CHIP_COLOR[u.role].fg,
                            fontWeight: 600,
                          }}
                        />
                      </Box>
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
            {(() => {
              const target = users.find((u) => u.id === backfillOwnerId);
              if (!target || target.role !== 'staff') return null;
              return (
                <Typography
                  sx={{ mt: 1, fontSize: '0.75rem', color: 'warning.dark' }}
                >
                  Heads up: this will make every legacy client and case visible
                  to <strong>{target.email}</strong> (Staff role can only see
                  what they own — so they will see all of it after this runs).
                </Typography>
              );
            })()}
          </Box>

          {unowned && (
            <Box
              sx={{
                px: 2,
                py: 1.5,
                borderRadius: 2,
                bgcolor: '#F9FAFB',
                border: '1px solid',
                borderColor: 'divider',
                fontSize: '0.8125rem',
                color: 'text.secondary',
              }}
            >
              <div>Clients to update: <strong>{unowned.clients}</strong></div>
              <div>Cases to update: <strong>{unowned.cases}</strong></div>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            variant="text"
            onClick={() => setBackfillDialogOpen(false)}
            disabled={backfilling}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleBackfill}
            disabled={backfilling || !backfillOwnerId}
            startIcon={
              backfilling ? <CircularProgress size={16} sx={{ color: '#FFFFFF' }} /> : null
            }
          >
            {backfilling
              ? 'Migrating…'
              : backfillOwnerId === staffUser?.id
              ? 'Assign all to me'
              : 'Run migration'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

/* -------------------------------------------------------------------------- */
/* Invite dialog                                                               */
/* -------------------------------------------------------------------------- */

interface InviteDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: (invite: Invitation) => void;
  createdBy: string;
  createdByEmail?: string;
}

const InviteDialog: React.FC<InviteDialogProps> = ({
  open,
  onClose,
  onCreated,
  createdBy,
  createdByEmail,
}) => {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Role>('staff');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setEmail('');
      setRole('staff');
      setError(null);
      setSubmitting(false);
    }
  }, [open]);

  const handleSubmit = async () => {
    setError(null);
    const trimmed = email.trim().toLowerCase();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setError('Enter a valid email address');
      return;
    }
    setSubmitting(true);
    try {
      const inv = await createInvitation(trimmed, role, createdBy, createdByEmail);
      onCreated(inv);
    } catch (err) {
      console.error(err);
      setError('Failed to create invitation. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        Invite staff
        <IconButton
          onClick={onClose}
          aria-label="Close"
          size="small"
          sx={{ position: 'absolute', right: 12, top: 12, color: 'text.secondary' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Box>
            <Typography
              component="label"
              htmlFor="invite-email"
              sx={{
                display: 'block',
                fontSize: '0.8125rem',
                fontWeight: 500,
                color: 'text.primary',
                mb: 1,
              }}
            >
              Email address
            </Typography>
            <TextField
              id="invite-email"
              type="email"
              fullWidth
              placeholder="colleague@example.com"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={submitting}
            />
          </Box>
          <Box>
            <Typography
              component="label"
              htmlFor="invite-role"
              sx={{
                display: 'block',
                fontSize: '0.8125rem',
                fontWeight: 500,
                color: 'text.primary',
                mb: 1,
              }}
            >
              Role
            </Typography>
            <FormControl fullWidth size="small">
              <InputLabel id="invite-role-label">Role</InputLabel>
              <Select
                labelId="invite-role-label"
                id="invite-role"
                value={role}
                label="Role"
                onChange={(e) => setRole(e.target.value as Role)}
                disabled={submitting}
              >
                {ROLES.map((r) => (
                  <MenuItem key={r} value={r}>
                    <Box>
                      <Typography sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
                        {ROLE_LABEL[r]}
                      </Typography>
                      <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                        {ROLE_DESCRIPTION[r]}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
            We'll generate a one-time invite link you can copy and send. The link
            expires after 7 days.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={submitting}
          startIcon={
            submitting ? <CircularProgress size={16} sx={{ color: '#FFFFFF' }} /> : null
          }
        >
          {submitting ? 'Creating…' : 'Create Invite'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default Staff;
